import { Deal, DealStateEnum } from '../gql/dealGql';
import { DocumentProgressStatus, Deal as GeneratedDeal } from '../gql/generated/graphql';

import { isUneditable } from '../constants/dealStates';

import { doesDealUseR1Contract } from './paperwork';

export const CONTRACT_NOT_FOUND_ERROR = 'Contract not found in R1';

const NON_VALIDATED_STATUSES = [
  DocumentProgressStatus.Required,
  DocumentProgressStatus.PendingR1,
  DocumentProgressStatus.PendingLender,
  DocumentProgressStatus.Failed,
];

const NON_UPLOADED_STATUSES = [
  DocumentProgressStatus.Required,
  DocumentProgressStatus.PendingR1,
  DocumentProgressStatus.PendingLender,
  DocumentProgressStatus.Failed,
  DocumentProgressStatus.GenerateDocuments,
];

const SENT_STATUSES = [DocumentProgressStatus.SentForSignatures, DocumentProgressStatus.Signed];

export const isDocumentProgressValidated = (
  documentProgressValidationStatus?: DocumentProgressStatus,
) =>
  documentProgressValidationStatus &&
  !NON_VALIDATED_STATUSES.includes(documentProgressValidationStatus);

export const isDocumentProgressUploaded = (
  documentProgressValidationStatus?: DocumentProgressStatus,
) =>
  documentProgressValidationStatus &&
  !NON_UPLOADED_STATUSES.includes(documentProgressValidationStatus);

export const isDocumentProgressSent = (documentProgressValidationStatus?: DocumentProgressStatus) =>
  documentProgressValidationStatus && SENT_STATUSES.includes(documentProgressValidationStatus);

export const getValidationMessage = (deal: Deal | GeneratedDeal) => {
  const {
    document_progress_status,
    r1_contract_validation_warnings_object,
    r1_contract_manually_validated,
  } = deal;

  switch (document_progress_status) {
    case DocumentProgressStatus.Failed:
      return { copy: 'Failed', textColor: 'errorsRed' };
    case DocumentProgressStatus.PendingR1:
      return { copy: 'In Progress (Route One)', textColor: 'ceruleanBlue' };
    case DocumentProgressStatus.PendingLender:
      return { copy: 'In Progress (Lienholder)', textColor: 'ceruleanBlue' };
    case DocumentProgressStatus.GenerateDocuments:
    case DocumentProgressStatus.UploadDocuments:
    case DocumentProgressStatus.ReadyForSignatures:
    case DocumentProgressStatus.SentForSignatures:
    case DocumentProgressStatus.Signed: {
      if (r1_contract_manually_validated) {
        return {
          copy: 'Performed Manually',
          textColor: 'americanOrange',
        };
      }
      if (r1_contract_validation_warnings_object?.warnings?.length) {
        return {
          copy: 'Warning. Review issues below and verify. You may proceed to generate contract.',
          textColor: 'ceruleanBlue',
        };
      }
      return { copy: 'Successful', textColor: 'caribbeanGreen.500' };
    }
    default:
      return { copy: 'Required', textColor: 'ceruleanBlue' };
  }
};

export const getShouldCancelContracts = (deal: Deal, newDealState: DealStateEnum | null) => {
  if (!newDealState) {
    return false;
  }

  const currentDealState = deal.state;
  const fromUneditableToEditableOrClosed =
    isUneditable(currentDealState) &&
    (!isUneditable(newDealState) || newDealState === DealStateEnum.Closed);
  const worksheetGenerated = deal.document_progress_status !== DocumentProgressStatus.Required;

  return fromUneditableToEditableOrClosed && worksheetGenerated && doesDealUseR1Contract(deal);
};
